import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { navLinks } from "../mock";
import { useLang, languages } from "../i18n";

function LangSwitcher({ scrolled }) {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center gap-1 rounded-full p-1 ${scrolled ? "bg-[#F0E6D6]" : "bg-white/15"}`}>
      {languages.map((l) => (
        <button
          key={l.code}
          onClick={() => setLang(l.code)}
          className={`px-2.5 py-1 rounded-full text-xs font-600 tracking-wide transition-all duration-300 ${
            lang === l.code
              ? "bg-[#9C3B2E] text-white"
              : scrolled
              ? "text-[#6b6058] hover:text-[#2A2420]"
              : "text-white/80 hover:text-white"
          }`}
        >
          {l.label}
        </button>
      ))}
    </div>
  );
}

export default function Navbar({ solid = false }) {
  const navigate = useNavigate();
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState("");

  const isSolid = solid || scrolled;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (window.location.pathname !== "/") return;
    const hashLinks = navLinks.filter((l) => l.href.startsWith("#"));
    const els = hashLinks
      .map((l) => document.querySelector(l.href))
      .filter(Boolean);
    if (!els.length) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(`#${e.target.id}`);
        });
      },
      { rootMargin: "-45% 0px -50% 0px", threshold: 0 }
    );
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  const go = (href) => {
    setOpen(false);
    if (href.startsWith("/") && !href.includes("#")) {
      navigate(href);
      return;
    }
    const hash = href.includes("#") ? `#${href.split("#")[1]}` : href;
    if (window.location.pathname !== "/") {
      navigate(`/${hash}`);
      setTimeout(() => {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }, 120);
      return;
    }
    const el = document.querySelector(hash);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        isSolid
          ? "bg-[#FAF4EA]/95 backdrop-blur-md shadow-[0_8px_30px_rgba(42,36,32,0.08)] py-3"
          : "bg-transparent py-6"
      }`}
    >
      <nav className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <button onClick={() => go("#home")} className="flex items-center gap-3 group">
          <img
            src="/logo.webp"
            alt="Quattro Tavoli"
            className={`w-14 h-14 rounded-full object-contain transition-all duration-500 group-hover:scale-105 ${
              isSolid ? "" : "bg-white/95 p-0.5 shadow-md"
            }`}
          />
          <span className="text-left leading-none">
            <span
              className={`font-courier text-xl font-bold tracking-tight block transition-colors duration-500 ${
                isSolid ? "text-[#2A2420]" : "text-white"
              }`}
            >
              Quattro Tavoli
            </span>
            <span
              className={`text-[10px] uppercase tracking-[0.35em] transition-colors duration-500 ${
                isSolid ? "text-[#9C3B2E]" : "text-white/70"
              }`}
            >
              München
            </span>
          </span>
        </button>

        {/* Desktop links */}
        <ul className="hidden lg:flex items-center gap-9">
          {navLinks.map((l) => (
            <li key={l.href}>
              <button
                onClick={() => go(l.href)}
                className={`relative text-sm tracking-wide font-500 transition-colors duration-300 group ${
                  active === l.href
                    ? "text-[#9C3B2E]"
                    : isSolid
                    ? "text-[#4A423B] hover:text-[#9C3B2E]"
                    : "text-white/90 hover:text-white"
                }`}
              >
                {t(l.key)}
                <span
                  className={`absolute -bottom-1.5 left-0 h-px bg-[#9C3B2E] transition-all duration-300 group-hover:w-full ${
                    active === l.href ? "w-full" : "w-0"
                  }`}
                />
              </button>
            </li>
          ))}
        </ul>

        <div className="hidden lg:flex items-center gap-4">
          <LangSwitcher scrolled={isSolid} />
          <button
            onClick={() => go("#visit")}
            className="inline-flex items-center px-6 py-2.5 rounded-full bg-[#9C3B2E] text-[#FAF4EA] text-sm font-500 tracking-wide hover:bg-[#9C3B2E] transition-all duration-300 hover:shadow-[0_10px_30px_-8px_rgba(181,86,58,0.7)] hover:-translate-y-0.5"
          >
            {t("nav.reserve")}
          </button>
        </div>

        {/* Mobile toggle */}
        <div className="lg:hidden flex items-center gap-3">
          <LangSwitcher scrolled={isSolid} />
          <button
            onClick={() => setOpen((v) => !v)}
            className={`p-2 rounded-md transition-colors ${isSolid ? "text-[#2A2420]" : "text-white"}`}
            aria-label="Menu"
          >
            {open ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
          </button>
        </div>
      </nav>

      {/* Mobile drawer */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 ${
          open ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
        }`}
      >
        <ul className="bg-[#FAF4EA] px-6 py-4 flex flex-col gap-1 border-t border-[#E7DAC6]">
          {navLinks.map((l) => (
            <li key={l.href}>
              <button
                onClick={() => go(l.href)}
                className="w-full text-left py-3 text-[#2A2420] font-500 border-b border-[#EFE4D2] last:border-0 hover:text-[#9C3B2E] transition-colors"
              >
                {t(l.key)}
              </button>
            </li>
          ))}
          <li className="pt-3">
            <button
              onClick={() => go("#visit")}
              className="w-full px-6 py-3 rounded-full bg-[#9C3B2E] text-[#FAF4EA] font-500"
            >
              {t("nav.reserve")}
            </button>
          </li>
        </ul>
      </div>
    </header>
  );
}
